import type { KyInstance } from "ky";
import { toSearchParams, type PaginatedRequest, type PaginatedResponse } from "./pagination.js";
import type {
  Gender,
  IsoDateTime,
  PractitionerAddressDto,
  PractitionerType,
  SessionFormat,
  StoredFileDto,
  TextData,
  WeeklyAvailability,
} from "./practitioners.js";
import type { TaxonomyOption } from "./taxonomy.js";

export type ApplicationStatus = "draft" | "pending" | "accepted" | "revoked";

export interface ApplicationIssue {
  field: string;
  message: string;
}

/**
 * A credential mid-application. Every field is optional because a draft is saved a step at a time —
 * completeness is checked once, at submit.
 */
export interface ApplicationCredentialDto {
  id: string;
  type?: PractitionerType;
  registeredBody?: TaxonomyOption;
  psychologyEndorsements: TaxonomyOption[];
  registrationNumber?: string;
  yearOfRegistration?: IsoDateTime;
  licenceExpiryDate?: IsoDateTime;
  licenceDocument?: StoredFileDto;
}

export interface ApplicationEducationDto {
  id: string;
  university?: string;
  degree?: string;
  fieldOfStudy?: string;
  yearStart?: IsoDateTime;
  yearEnd?: IsoDateTime;
}

export interface ApplicationCertificationDto {
  id: string;
  name?: string;
  issuingBody?: string;
  yearQualified?: IsoDateTime;
  file?: StoredFileDto;
}

/**
 * An application, whatever its status. The profile fields carry the same names they will have on
 * `PractitionerProfileDto` once accepted — acceptance is a conversion, not a remapping.
 *
 * There is no public variant: an application is only ever read by the applicant or a reviewer.
 */
export interface ApplicationDto {
  id: string;
  practitionerId: string;
  status: ApplicationStatus;

  firstName?: string;
  lastName?: string;
  title?: TaxonomyOption;

  profilePhotoUrl?: string;

  focusAreas: TaxonomyOption[];
  otherFocusAreas: TaxonomyOption[];
  therapeuticApproaches: TaxonomyOption[];
  culturalBackground: TaxonomyOption[];
  languages: TaxonomyOption[];
  ageGroups: TaxonomyOption[];
  clientTypes: TaxonomyOption[];
  fundingPathways: TaxonomyOption[];
  thirdPartySchemes: TaxonomyOption[];

  credentials: ApplicationCredentialDto[];
  education: ApplicationEducationDto[];
  certifications: ApplicationCertificationDto[];

  careerBreakYears?: number;

  about?: TextData;

  sessionFormats: SessionFormat[];
  practiceAddresses: PractitionerAddressDto[];
  timezone?: string;
  availability: WeeklyAvailability;

  phoneE164?: string;
  primaryEmail?: string;
  websiteUrl?: string;

  gender?: Gender;
  isAcceptingNewClients: boolean;

  /** Why a revoked application was sent back, and what a resubmission has to fix. */
  issues: ApplicationIssue[];

  /** The account's sign-in address, which is where correspondence goes. */
  applicantEmail: string;

  submittedAt?: IsoDateTime;
  processedAt?: IsoDateTime;
  processedBy?: string;
  note?: string;

  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/**
 * The body of `PATCH /applications/@me`.
 *
 * Taxonomy selections are **slugs**, the same ones `GET /taxonomy` hands the picker — the API
 * resolves each list against the field type its key names. Files are two-step: the upload returns a
 * storage id and only that id is patched, which is why a credential writes `licenceDocumentId` where
 * it reads `licenceDocument`.
 *
 * An absent key is left alone; a present one replaces. So an empty array clears.
 */
export interface SaveApplicationRequest {
  firstName?: string;
  lastName?: string;
  title?: string;

  focusAreas?: string[];
  otherFocusAreas?: string[];
  therapeuticApproaches?: string[];
  culturalBackground?: string[];
  languages?: string[];
  ageGroups?: string[];
  clientTypes?: string[];
  fundingPathways?: string[];
  thirdPartySchemes?: string[];

  credentials?: SaveApplicationCredential[];
  education?: SaveApplicationEducation[];
  certifications?: SaveApplicationCertification[];

  careerBreakYears?: number;
  /** `null` clears the stored document — an absent key leaves it alone. The draft write path
   *  distinguishes the two, so the editor can empty an About it previously saved. */
  about?: TextData | null;

  sessionFormats?: SessionFormat[];
  practiceAddresses?: SaveApplicationAddress[];
  timezone?: string;
  availability?: WeeklyAvailability;

  phoneE164?: string;
  primaryEmail?: string;
  websiteUrl?: string;

  gender?: Gender;
  isAcceptingNewClients?: boolean;
}

export interface SaveApplicationCredential {
  /** Echoed back so the API can keep the licence already attached to this row. */
  id?: string;
  type?: PractitionerType;
  registeredBody?: string;
  psychologyEndorsements?: string[];
  registrationNumber?: string;
  yearOfRegistration?: IsoDateTime;
  licenceExpiryDate?: IsoDateTime;
  licenceDocumentId?: string;
}

export interface SaveApplicationEducation {
  id?: string;
  university?: string;
  degree?: string;
  fieldOfStudy?: string;
  yearStart?: IsoDateTime;
  yearEnd?: IsoDateTime;
}

export interface SaveApplicationCertification {
  id?: string;
  name?: string;
  issuingBody?: string;
  yearQualified?: IsoDateTime;
  fileId?: string;
}

export interface SaveApplicationAddress {
  id?: string;
  countryCode?: string;
  administrativeAreaLevel1?: string;
  administrativeAreaLevel2?: string;
  postcode?: string;
  locationName?: string;
  formattedAddress?: string;
  lat?: number;
  lng?: number;
}

/**
 * What `submit` answers with when the draft is incomplete. Not an error: completeness is checked
 * once, at submit, and a draft that fails the check is a normal outcome the form has to render — so
 * the issues come back `200` and nothing is written beyond `application_issues`.
 */
export interface ApplicationRejection {
  issues: ApplicationIssue[];
}

export type SubmitApplicationResponse = ApplicationDto | ApplicationRejection;

/**
 * Discriminates on `status` rather than on `issues`, because a successfully submitted application
 * carries an `issues` array too — the ones it was previously revoked for.
 */
export const isApplicationRejection = (
  response: SubmitApplicationResponse,
): response is ApplicationRejection => !("status" in response);

/** Columns `GET /admin/applications` will sort on. Anything else is rejected by the API. */
export type ApplicationSortKey = "fullName" | "status" | "submittedAt" | "createdAt";

/**
 * One row of the admin queue. Deliberately not an `ApplicationDto` — the list draws four columns and
 * has no use for the profile.
 */
export interface ApplicationListItem {
  id: string;
  status: ApplicationStatus;
  fullName: string | null;
  /** The applicant's contact email, falling back to their account email while the draft is empty. */
  email: string | null;
  submittedAt: IsoDateTime | null;
  createdAt: IsoDateTime;
}

export interface ListApplicationsQuery extends PaginatedRequest {
  sortBy?: ApplicationSortKey;
  /**
   * Narrows to one lifecycle state. **Omitting it excludes drafts** — a draft row is created the
   * moment a practitioner first opens the form, so an unfiltered queue would be mostly empty rows
   * nobody has submitted. Pass `"draft"` explicitly to see them.
   */
  status?: ApplicationStatus;
}

export interface AcceptApplicationRequest {
  note?: string;
}

export interface RevokeApplicationRequest {
  note?: string;
  issues: ApplicationIssue[];
}

export interface UploadedFileRef {
  id: string;
  key: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  isPublic: boolean;
  uploadedBy: string;
  createdAt: string;
  url: string;
}

export class ApplicationApi {
  constructor(private readonly http: KyInstance) { }

  mine = (): Promise<ApplicationDto> =>
    this.http.get("applications/@me").json<ApplicationDto>();

  saveDraft = (patch: SaveApplicationRequest): Promise<ApplicationDto> =>
    this.http.patch("applications/@me", { json: patch }).json<ApplicationDto>();

  submit = (): Promise<SubmitApplicationResponse> =>
    this.http.post("applications/@me/submit").json<SubmitApplicationResponse>();

  uploadProfilePhoto = (file: Blob): Promise<ApplicationDto> => {
    const body = new FormData();
    body.set("file", file);

    return this.http.post("applications/@me/profile-photo", { body }).json<ApplicationDto>();
  };

  uploadDocument = (file: Blob): Promise<UploadedFileRef> =>
    this.upload("applications/@me/documents", file);

  list = (query: ListApplicationsQuery = {}): Promise<PaginatedResponse<ApplicationListItem>> =>
    this.http
      .get("admin/applications", { searchParams: toSearchParams({ ...query }) })
      .json<PaginatedResponse<ApplicationListItem>>();

  byId = (id: string): Promise<ApplicationDto> =>
    this.http.get(`admin/applications/${encodeURIComponent(id)}`).json<ApplicationDto>();

  accept = (id: string, body: AcceptApplicationRequest = {}): Promise<ApplicationDto> =>
    this.http
      .post(`admin/applications/${encodeURIComponent(id)}/accept`, { json: body })
      .json<ApplicationDto>();

  revoke = (id: string, body: RevokeApplicationRequest): Promise<ApplicationDto> =>
    this.http
      .post(`admin/applications/${encodeURIComponent(id)}/revoke`, { json: body })
      .json<ApplicationDto>();

  private upload = (path: string, file: Blob): Promise<UploadedFileRef> => {
    const body = new FormData();
    body.set("file", file);

    return this.http.post(path, { body }).json<UploadedFileRef>();
  };
}
